// "These 3 threads look like one piece of work: Insurance Renewal" (busy-my-web#275).
//
// AI Brain spec Appendix §3: when several SELECTED threads relate, Busy.me offers to create — or
// join — an Ongoing with a proposed name, and that Ongoing holds those threads and only those. It is
// the opposite half of the email-session's other create: `createTargets` in lib/emailSession.js
// follows SENDERS ("everything Dr Smith Surgery sends"), this one holds THREADS ("these three mails
// about the renewal"). Both live in the same panel, so keeping them in separate files is what stops
// one quietly becoming the other.
//
// Everything here is pure — no store, no api, no timers of its own (the debouncer takes them) — so
// "when is the offer shown", "what does it say" and "what is sent" are answerable without mounting
// anything.

/**
 * Below this cohesion there is no offer. It is the BFF's own floor (`ThreadOngoingOffer`), which is
 * in turn timeline-srv's weak-evidence ceiling: the highest two threads can score on a shared sender
 * alone. The web checks it a second time rather than trusting the answer, because a row that
 * proposes a name over unrelated mail is the exact failure this feature is judged on.
 */
export const COHESION_FLOOR = 0.4

/** An offer is about two threads or more — one thread is not "these threads". */
export const MIN_THREADS = 2

/** How long a selection must hold still before it is worth asking about. */
export const DEBOUNCE_MS = 400

/** Verbatim strings, kept with the rest of the session's wording (lib/emailSession.js TEXT). */
export const TEXT = {
  create: 'Create Ongoing',
  // The promise busy-timeline-srv#21 made and this feature keeps: the picked threads are linked, and
  // what arrives later is OFFERED. Worded as the create step's own helper is, so the two agree.
  linkedHelper: 'Future emails about this will be suggested here, so you choose what belongs.',
}

/**
 * The timeline ids of the email threads on the Work-board. Group picks are not threads — a group is
 * a sender, and following senders is the other feature.
 */
export function pickedThreadIds(items) {
  const ids = []
  for (const item of items || []) {
    if (!item || item.kind !== 'email-pick') continue
    const id = item.emailId || item.objectRef?.id
    if (id && !ids.includes(id)) ids.push(id)
  }
  return ids
}

/**
 * What identifies a SELECTION rather than an order of ticking: the ids sorted and joined. Ticking A
 * then B and B then A is one question, and asking it twice is a wasted model call.
 */
export function selectionSignature(ids) {
  return [...(ids || [])].map(String).sort().join(',')
}

/** Is this selection worth asking about at all? */
export function shouldSuggest(ids) {
  return (ids || []).length >= MIN_THREADS
}

/** The body of `POST ongoing/suggest-from-threads`. */
export function suggestRequest(ids) {
  return { timelineIds: [...(ids || [])] }
}

/**
 * The BFF's answer, normalised — or null when there is nothing to offer. An empty answer is the
 * normal outcome for a selection that does not hang together, never an error, so it reads as "no
 * suggestion" here and nowhere else has to know the shape.
 */
export function readSuggestion(payload) {
  const data = payload?.data?.data ?? payload?.data ?? payload ?? null
  const suggestion = data?.suggestion
  const title = String(suggestion?.title || '').trim()
  if (!title) return null
  const threads = (suggestion.threads || []).filter(Boolean)
  const join = data?.join?.ongoingId
    ? {
      ongoingId: data.join.ongoingId,
      title: String(data.join.title || '').trim(),
      confidence: Number(data.join.confidence) || 0,
    }
    : null
  return {
    title,
    rationale: String(suggestion.rationale || '').trim(),
    cohesion: Number(suggestion.cohesion) || 0,
    threads,
    join,
  }
}

/**
 * Is the offer strong enough to show? Below the floor, or about fewer than two threads, the row is
 * absent — not greyed out. An offer nobody should take is not an offer.
 */
export function isVisible(suggestion) {
  if (!suggestion?.title) return false
  if ((suggestion.threads || []).length < MIN_THREADS) return false
  return (Number(suggestion.cohesion) || 0) >= COHESION_FLOOR
}

/** "3 threads" / "1 thread". */
export function threadsLabel(n) {
  const count = Number(n) || 0
  return `${count} thread${count === 1 ? '' : 's'}`
}

/** The row's own sentence, in the spec's words. */
export function suggestionText(suggestion) {
  const count = (suggestion?.threads || []).length
  return `These ${threadsLabel(count)} look like one piece of work: ${suggestion?.title || ''}`
}

/** "Add to Insurance Renewal" — the second verb, only when there is a story to join. */
export function joinLabel(join) {
  const title = String(join?.title || '').trim()
  return title ? `Add to ${title}` : ''
}

/**
 * The suggestion row, in the same shape the panel's other options use (`suggestions()` in
 * lib/emailSession.js) plus the two actions this one carries. Null when the offer is not visible, so
 * the caller can spread it into the list without a second condition.
 */
export function suggestionRow(suggestion) {
  if (!isVisible(suggestion)) return null
  return {
    id: 'thread-ongoing',
    text: suggestionText(suggestion),
    recommended: true,
    ready: true,
    title: suggestion.title,
    rationale: suggestion.rationale,
    threads: [...suggestion.threads],
    join: suggestion.join || null,
    joinLabel: joinLabel(suggestion.join),
  }
}

/** The naming step's targets for a thread-built create — one row per thread, as the panel draws it. */
export function threadTargets(items, suggestion = null) {
  const wanted = suggestion?.threads?.length ? new Set(suggestion.threads.map(String)) : null
  return (items || [])
    .filter(i => i && i.kind === 'email-pick' && (!wanted || wanted.has(String(i.emailId))))
    .map(i => ({
      id: i.id,
      timelineId: i.emailId,
      // The subject is what this thread IS; the group name is who it is from, and is the fallback.
      name: i.subject || i.title || '',
      count: 1,
    }))
}

/** The body of `POST ongoing/from-threads`: the title as edited, and exactly these threads. */
export function createRequest(title, targets) {
  return {
    timelineIds: (targets || []).map(t => t.timelineId || t).filter(Boolean),
    title: String(title || '').trim(),
    // Never true from here. The spec's rule is that this Ongoing holds the threads that were picked
    // and never absorbs mail by itself; asking for auto-connect is a decision made on the story's own
    // automation switch, later, by somebody who has read what it does.
    autoConnectNewThreads: false,
  }
}

/**
 * The naming step's link-confirmation block for a thread-built create — the same shape
 * `linkConfirmation` in lib/emailSession.js returns for groups, so one template draws both, saying
 * what is true here: threads are linked, and future mail is offered rather than taken.
 */
export function linkConfirmation(targets) {
  const list = targets || []
  if (!list.length) return null
  return {
    text: `Link ${threadsLabel(list.length)}`,
    emails: `${list.length} email${list.length === 1 ? '' : 's'}`,
    helper: TEXT.linkedHelper,
  }
}

/** A create needs a name and at least one thread. */
export function canCreate(title, targets) {
  return String(title || '').trim().length > 0 && (targets || []).length > 0
}

/**
 * Joining an existing story, as TWO plans, because the estate has two ways to do it and only one of
 * them exists on every deployment:
 *
 *   • `bulk`     — `POST ongoing/{id}/threads`, one call for the lot. It belongs to the Work-board
 *                  work and may not be there yet;
 *   • `fallback` — `POST ongoing/{id}/timelines` per thread, the link every story page already uses.
 *
 * Both are described here so the caller's job is "try the first, then the second" and neither URL is
 * spelled out in a component.
 */
export function joinPlan(join, targets) {
  const ongoingId = join?.ongoingId || join
  const timelineIds = (targets || []).map(t => t.timelineId || t).filter(Boolean)
  if (!ongoingId || !timelineIds.length) return null
  return {
    ongoingId,
    timelineIds,
    bulk: { url: `ongoing/${ongoingId}/threads`, body: { timelineIds } },
    fallback: timelineIds.map(timelineId => ({
      url: `ongoing/${ongoingId}/timelines`,
      // Confirmed, not suggested: the user pressed "Add to …", which is an answer, not a guess.
      body: { timelineId, isSuggested: false },
    })),
  }
}

/**
 * The receipt. It says what happened to the THREADS and what will happen to mail that has not
 * arrived yet — the second sentence is busy-timeline-srv#21's promise, repeated here because this is
 * the moment the user is deciding whether to trust it.
 */
export function linkedNotice(count, title) {
  const name = String(title || '').trim()
  return {
    kind: 'linked',
    text: `${threadsLabel(count)} linked to ${name}`,
    helper: TEXT.linkedHelper,
  }
}

/**
 * The debounced, de-duplicated suggest call.
 *
 * Two rules, and they are the reason this is not a `setTimeout` in the store:
 *   • a selection that is still being made is not asked about — every tick restarts the wait;
 *   • the same SELECTION is asked about once. Untick and re-tick the same three threads and the
 *     answer has not changed, so nothing is sent.
 *
 * The timers are injectable, so a test can drive it without waiting.
 *
 * @param run called with (ids, signature) when the wait expires.
 */
export function createSuggestionRunner(run, { delay = DEBOUNCE_MS, setTimer = setTimeout, clearTimer = clearTimeout } = {}) {
  let handle = null
  let asked = null

  const cancel = () => {
    if (handle !== null) clearTimer(handle)
    handle = null
  }

  return {
    /**
     * Ask about this selection, eventually. Answers the signature that will be run — null when
     * nothing will be, because the selection is too small or is the one already asked about.
     */
    schedule(ids) {
      const list = [...(ids || [])]
      if (!shouldSuggest(list)) {
        cancel()
        asked = null
        return null
      }
      const signature = selectionSignature(list)
      if (signature === asked) return null
      cancel()
      asked = signature
      handle = setTimer(() => {
        handle = null
        run(list, signature)
      }, delay)
      return signature
    },

    /** Forget what was asked — after a create, a join or a failure, so the same selection asks again. */
    reset() {
      cancel()
      asked = null
    },

    cancel,

    /** The signature currently asked about, for a caller that wants to drop a stale answer. */
    get signature() { return asked },
  }
}
